"use client";

import { PORTALS } from ".constants/portals";
import {
  PortalModal,
  ModalHeader,
  ModalContent,
  usePortalContext,
} from "@cloai/uikit";
import { motion } from "framer-motion";
import { map } from "lodash";
import { useLocale } from "next-intl";
import Image from "next/image";
import { memo, useCallback } from "react";

import {
  PhotoSettings,
  useUndressContext,
  useUndressSettingsContext,
} from "@/providers";
import { cn } from "@/utils/cn";
import { getLocalizedName } from "@/utils/getLocalizedName";

import { STYLES, MOTION_VARIANTS } from "./vars";

export const CostumesPortal = memo(() => {
  const {
    clickFileId,
    clickSettings,
    clickUndressingFile,
    initialSettings,
    onReUseFaceObj,
    reUseFaceObj,
    updatePhotoSetting,
  } = useUndressContext();
  const { costumes } = useUndressSettingsContext();
  const { closePortal, openPortal } = usePortalContext();

  const locale = useLocale();

  const { pose } = clickSettings;

  const handleClick = useCallback(
    (itemType: keyof PhotoSettings, itemId: string) => () => {
      updatePhotoSetting(clickFileId, itemType, itemId);
      closePortal();

      if (pose === initialSettings.pose) {
        return openPortal(PORTALS.USER_FLOW_POSES);
      }
      if (reUseFaceObj) {
        return onReUseFaceObj();
      }

      if (!clickUndressingFile) {
        return openPortal(PORTALS.USER_FLOW_UPLOAD);
      }
    },
    [
      clickFileId,
      clickUndressingFile,
      closePortal,
      initialSettings.pose,
      onReUseFaceObj,
      openPortal,
      pose,
      reUseFaceObj,
      updatePhotoSetting,
    ],
  );

  return (
    <PortalModal
      portalId={PORTALS.USER_FLOW_COSTUMES}
      style={STYLES.portal.variants.wide}
    >
      <ModalHeader>CHOOSE COSTUME</ModalHeader>

      <ModalContent className="@container">
        <motion.div
          variants={MOTION_VARIANTS.container}
          initial="hidden"
          animate="visible"
          className={cn(
            "grid grid-cols-2 gap-2",
            "@lg:grid-cols-3",
            "@2xl:grid-cols-4 @2xl:gap-3",
          )}
        >
          {map(costumes, (item, Index) => (
            <motion.div
              key={item.id}
              variants={MOTION_VARIANTS.item}
              custom={Index}
            >
              <div
                className={cn(
                  STYLES.wrapper.base,
                  STYLES.wrapper.variants.square,
                )}
                onClick={handleClick("costume", item.id)}
              >
                <Image
                  src={item.image ?? ""}
                  fill
                  sizes="(min-width: 768px) 25vw, 50vw"
                  alt=""
                  priority
                  className={STYLES.image}
                />
                <span
                  className={cn(STYLES.text.base, STYLES.text.variants.default)}
                >
                  {getLocalizedName(item, locale)}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </ModalContent>
    </PortalModal>
  );
});

CostumesPortal.displayName = "CostumesPortal";
